import { onBeforeUnmount, onMounted, ref, watch, type Ref } from 'vue'

interface TypewriterOptions {
  /** Static text in front of every phrase, e.g. 'Search for '. */
  prefix?: string
  /** While true the animation rests on the current phrase (the field has text, so it's hidden anyway). */
  paused?: Ref<boolean>
  typeMs?: number
  deleteMs?: number
  holdMs?: number
  gapMs?: number
}

/**
 * A placeholder that types an example phrase, holds it, deletes it and moves on
 * to the next. It starts on the full first phrase so the field never looks empty,
 * and users who prefer reduced motion only ever see that first phrase.
 */
export function useTypewriterPlaceholder(
  phrases: readonly [string, ...string[]],
  options: TypewriterOptions = {},
) {
  const { prefix = '', paused, typeMs = 70, deleteMs = 35, holdMs = 1800, gapMs = 400 } = options

  const placeholder = ref(prefix + phrases[0])
  let index = 0
  let length = phrases[0].length
  let deleting = false
  let timer: ReturnType<typeof setTimeout> | undefined

  function render() {
    placeholder.value = prefix + phrases[index].slice(0, length)
  }

  function step() {
    const phrase = phrases[index]
    if (!deleting && length < phrase.length) {
      length++
      render()
      timer = setTimeout(step, typeMs)
    } else if (!deleting) {
      deleting = true
      timer = setTimeout(step, holdMs)
    } else if (length > 0) {
      length--
      render()
      timer = setTimeout(step, deleteMs)
    } else {
      deleting = false
      index = (index + 1) % phrases.length
      timer = setTimeout(step, gapMs)
    }
  }

  function stop() {
    clearTimeout(timer)
    timer = undefined
  }

  function start() {
    stop()
    length = phrases[index].length
    deleting = false
    render()
    step()
  }

  let animate = false

  onMounted(() => {
    animate = phrases.length > 1 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (animate && !paused?.value) start()
  })

  if (paused) {
    watch(paused, (isPaused) => {
      if (!animate) return
      if (isPaused) stop()
      else start()
    })
  }

  onBeforeUnmount(stop)

  return placeholder
}
